import React, { useState } from 'react';
import { UserProfile, FinancialConfig } from '../types';
import { MapPin, Briefcase, TrendingUp, DollarSign, Clock, ArrowRight, ChevronDown, Search, Zap } from 'lucide-react';
import { NICHES } from '../constants';
import { categorizeNiche } from '../utils/nicheUtils';
import { useProfessions } from '../hooks/useProfessions';
import FontSizeSelector from './FontSizeSelector';

interface Props {
  onComplete: (user: UserProfile, config: FinancialConfig) => void;
  initialUser?: UserProfile | null;
  initialConfig?: FinancialConfig | null;
}

export default function Onboarding({ onComplete, initialUser, initialConfig }: Props) {
  const [step, setStep] = useState(1);
  const { professions, searchProfessions } = useProfessions();
  const [user, setUser] = useState<Partial<UserProfile>>(initialUser || {
    id: (initialUser as any)?.id ?? crypto.randomUUID(),
    actingType: 'Novo na área',
    ownerName: '',
    profession: '',
    niche: '',
    city: '',
    state: ''
  });
  const [config, setConfig] = useState<Partial<FinancialConfig>>(initialConfig || {
    proLaboreGoal: 2000,
    monthlyFixedCost: 500,
    weeklyWorkHours: 44,
  });
  
  const [isNicheOpen, setIsNicheOpen] = useState(false);
  const [isTaxRegimeOpen, setIsTaxRegimeOpen] = useState(false);

  const filteredProfessions = user.profession 
    ? searchProfessions(user.profession, 12) 
    : professions.slice(0, 12);
  
  const taxRegimes = [
    "Sou MEI",
    "Tenho empresa pequena",
    "Trabalho sozinho (Autônomo)"
  ];

  const calculateBaseHour = () => {
    const totalMonthlyCost = (config.proLaboreGoal || 0) + (config.monthlyFixedCost || 0);
    const totalMonthlyHours = (config.weeklyWorkHours || 44) * 4.33;
    if (!totalMonthlyHours || totalMonthlyHours <= 0) return 0;
    return totalMonthlyCost / totalMonthlyHours;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };


  const handleNext = () => {
    if (step === 1) {
      if (!user.ownerName || !user.profession || !user.city) {
        alert('Por favor, preencha todos os campos para continuar.');
        return;
      }
      setStep(2);
    } else {
      // Calculate Base Hour Value
      const totalMonthlyCost = (config.proLaboreGoal || 0) + (config.monthlyFixedCost || 0);
      const totalMonthlyHours = (config.weeklyWorkHours || 44) * 4.33;
      const baseHourValue = totalMonthlyCost / totalMonthlyHours;

      const finalConfig: FinancialConfig = {
        userId: user.id!,
        proLaboreGoal: config.proLaboreGoal || 0,
        monthlyFixedCost: config.monthlyFixedCost || 0,
        weeklyWorkHours: config.weeklyWorkHours || 0,
        baseHourValue: Number(baseHourValue.toFixed(2)),
      };

      const safeUser = { 
        ...user, 
        ownerName: user.ownerName || 'Usuário', 
        niche: user.niche || 'Geral', 
        id: user.id || crypto.randomUUID() 
      } as UserProfile;

      onComplete(safeUser, finalConfig);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-brand p-6 text-white">
          <h1 className="text-2xl font-bold">Configurando o R MAX para você</h1>
          <p className="text-brand/80 mt-1">
            {step === 1 ? 'Seus números reais' : 'Vamos começar seu perfil'}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {step === 1 ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4">
              <div className="relative z-30">
                <label className="block text-sm font-medium text-zinc-700 mb-1">Qual sua profissão?</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-3.5 h-5 w-5 text-zinc-400 pointer-events-none" />
                  <input
                    type="text"
                    autoComplete="off"
                    className="w-full pl-10 pr-10 py-3 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-brand focus:border-brand outline-none transition-all placeholder:text-zinc-400 text-sm"
                    placeholder="Ex: Boleira, Pedreiro, Designer..."
                    value={user.profession || ''}
                    onFocus={() => setIsNicheOpen(true)}
                    onBlur={() => setTimeout(() => setIsNicheOpen(false), 200)}
                    onChange={(e) => {
                      const val = e.target.value;
                      setUser({ ...user, profession: val });
                      setIsNicheOpen(true);
                    }}
                  />
                  <ChevronDown className={`absolute right-3 top-3.5 h-5 w-5 text-zinc-400 pointer-events-none transition-transform duration-200 ${isNicheOpen ? 'rotate-180' : ''}`} />
                  
                  {isNicheOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-100 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto w-full z-50 animate-in fade-in slide-in-from-top-2">
                       {filteredProfessions.length > 0 ? (
                        filteredProfessions.map((p) => (
                          <div
                            key={p.name}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setUser({ 
                                ...user, 
                                profession: p.name, 
                                niche: p.niche,
                                nicheCategory: p.niche 
                              });
                              setIsNicheOpen(false);
                            }}
                            className={`px-4 py-3 cursor-pointer transition-colors text-sm border-b border-zinc-50 last:border-none hover:bg-zinc-50 hover:text-brand ${
                              user.profession === p.name ? 'bg-brand/5 text-brand font-bold' : 'text-zinc-600'
                            }`}
                          >
                            <div className="font-medium">{p.name}</div>
                            <div className="text-[10px] text-zinc-400 uppercase tracking-tighter">{p.niche}</div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-xs text-zinc-400 italic">
                          Nenhuma profissão sugerida. O sistema tentará mapear automaticamente.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {user.profession && (
                  <div className="mt-2.5 p-3 bg-blue-50/80 border border-blue-200/80 rounded-xl flex items-center gap-2.5 text-xs text-blue-900 animate-in fade-in">
                    <Zap className="w-4 h-4 text-blue-600 shrink-0" />
                    <div>
                      <span className="font-bold">Segmento Reconhecido: </span>
                      <span>O R-Max configurou as ferramentas ideais para <strong className="text-blue-950 font-black">{user.profession}</strong>.</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-20">
                <label className="block text-sm font-medium text-zinc-700 mb-1">Como você trabalha?</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-3.5 h-5 w-5 text-zinc-400" />
                  <button
                    type="button"
                    onClick={() => setIsTaxRegimeOpen(!isTaxRegimeOpen)}
                    onBlur={() => setTimeout(() => setIsTaxRegimeOpen(false), 200)}
                    className="w-full pl-10 pr-10 py-3 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-brand focus:border-brand outline-none transition-all bg-white text-left text-sm"
                  >
                    <span className={user.taxRegime ? 'text-zinc-900' : 'text-zinc-400'}>
                      {user.taxRegime || 'Selecione sua forma de atuação'}
                    </span>
                  </button>
                  <ChevronDown className={`absolute right-3 top-3.5 h-5 w-5 text-zinc-400 pointer-events-none transition-transform duration-200 ${isTaxRegimeOpen ? 'rotate-180' : ''}`} />
                  
                  {isTaxRegimeOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-100 rounded-xl shadow-xl overflow-hidden z-40 animate-in fade-in slide-in-from-top-2">
                      {taxRegimes.map((regime) => (
                        <div
                          key={regime}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setUser({ ...user, taxRegime: regime });
                            setIsTaxRegimeOpen(false);
                          }}
                          className={`px-4 py-3 cursor-pointer transition-colors text-sm border-b border-zinc-50 last:border-none hover:bg-brand/10 hover:text-brand-dark ${
                            user.taxRegime === regime ? 'bg-brand/10 text-brand-dark font-medium' : 'text-zinc-600'
                          }`}
                        >
                          {regime}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Cidade</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3.5 h-5 w-5 text-zinc-400" />
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-brand focus:border-brand outline-none transition-all"
                      placeholder="Sua cidade"
                      value={user.city || ''}
                      onChange={(e) => setUser({ ...user, city: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Estado</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-brand focus:border-brand outline-none transition-all"
                    placeholder="UF"
                    maxLength={2}
                    value={user.state || ''}
                    onChange={(e) => setUser({ ...user, state: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Como quer ser chamado?</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-brand focus:border-brand outline-none transition-all"
                  placeholder="Seu nome ou nome da empresa"
                  value={user.ownerName || ''}
                  onChange={(e) => setUser({ ...user, ownerName: e.target.value, fullName: e.target.value })}
                />
              </div>

              <div className="pt-2 border-t border-zinc-100">
                <FontSizeSelector />
              </div>

            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Vamos descobrir quanto vale a sua hora de trabalho. Seja sincero nos valores para não ter prejuízo no fim do mês!
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Quanto você quer tirar de salário livre por mês?</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-3.5 h-5 w-5 text-zinc-400" />
                  <input
                    type="number"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-brand focus:border-brand outline-none transition-all"
                    placeholder="Seu salário livre"
                    value={config.proLaboreGoal || ''}
                    onChange={(e) => setConfig({ ...config, proLaboreGoal: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Quanto você gasta por mês para manter o negócio (Aluguel, Luz, Internet)?</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-3.5 h-5 w-5 text-zinc-400" />
                  <input
                    type="number"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-brand focus:border-brand outline-none transition-all"
                    placeholder="Contas totais do mês"
                    value={config.monthlyFixedCost || ''}
                    onChange={(e) => setConfig({ ...config, monthlyFixedCost: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Horas de Trabalho (Semanal)</label>
                <div className="relative">
                  <Clock className="absolute left-3 top-3.5 h-5 w-5 text-zinc-400" />
                  <input
                    type="number"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-brand focus:border-brand outline-none transition-all"
                    placeholder="Quantas horas por semana?"
                    value={config.weeklyWorkHours || ''}
                    onChange={(e) => setConfig({ ...config, weeklyWorkHours: Number(e.target.value) })}
                  />
                </div>
              </div>

              {/* Real-time Base Hour Value Display */}
              <div className="bg-brand rounded-2xl p-5 shadow-lg border border-brand/50 mt-6 animate-in zoom-in-95 duration-300">
                <p className="text-brand/80 text-xs mb-1 font-medium uppercase tracking-wider">Sua hora de trabalho vale:</p>
                <div className="flex items-baseline gap-2 text-white">
                  <span className="text-3xl font-bold">
                    {formatCurrency(calculateBaseHour())}
                  </span>
                  <span className="text-brand/70 text-sm">/h</span>
                </div>
                <p className="text-[10px] text-brand/50 mt-2 leading-relaxed">
                  Este valor cobre seu pro-labore e custos fixos. Use-o como base para seus orçamentos.
                </p>
              </div>
            </div>
          )}

          <button
            onClick={handleNext}
            className="w-full bg-brand hover:bg-brand-dark text-white font-medium py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {step === 1 ? 'Próximo Passo' : 'Começar'}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
